// โพสต์ KOL: รูปจริงจากโพสต์ เรียงตามยอดวิว
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Brand } from "@contracts/api";
import { useCampaigns } from "@features/campaigns/api/use-campaigns";
import { wazzupToken } from "@features/inbox/api/inbox-api";
import { fmtCompact, fmtInt } from "@shared/lib/format";
import { cn } from "@shared/lib/utils";
import { fetchKolPosts, overviewKeys, type KolPostWithRef } from "../api/overview-api";
import { OvLoading, OvNote } from "../components/ov-states";
import { kolRefsOf } from "../lib/kol-refs";

export function KolPostsBlock({ brand, days }: { brand: Brand; days: number }) {
  const { data: campaigns } = useCampaigns(brand.id);
  const refs = kolRefsOf(campaigns);
  const hasToken = !!wazzupToken();

  const { data, isPending } = useQuery({
    queryKey: overviewKeys.kolPosts(refs, days),
    queryFn: () => fetchKolPosts(refs, days),
    enabled: hasToken && refs.length > 0,
  });

  if (!hasToken) return <OvNote>ต้องเข้าสู่ระบบ Wazzup ก่อน</OvNote>;
  if (!refs.length) return <OvNote>ยังไม่ได้ผูกรายงาน KOL กับแคมเปญของแบรนด์นี้</OvNote>;
  if (isPending) return <OvLoading />;

  const posts = data ?? [];
  if (!posts.length)
    return (
      <OvNote>
        {days
          ? `ไม่มีโพสต์ KOL ใน ${days} วันนี้ — ลองขยายช่วงด้านบน`
          : "ยังไม่มีโพสต์ที่ดึงข้อมูลได้"}
      </OvNote>
    );

  return (
    <>
      <div className="ov-kols">
        {posts.slice(0, 12).map((p, i) => (
          <KolCard key={`${p.ref}-${p.url || i}`} p={p} />
        ))}
      </div>
      {posts.length > 12 && (
        <p className="muted small-note">แสดง 12 จาก {fmtInt(posts.length)} โพสต์</p>
      )}
    </>
  );
}

function KolCard({ p }: { p: KolPostWithRef }) {
  // รูปบางตัวดึงไม่ได้แม้ผ่าน proxy (URL เซ็นชื่อของ CDN หมดอายุ) — แยกป้ายให้ตรงเหตุ
  const [imgFailed, setImgFailed] = useState(false);
  const [avaFailed, setAvaFailed] = useState(false);

  return (
    <a className="ov-kol" href={p.url || "#"} target="_blank" rel="noopener">
      <span className={cn("ov-kol-img", !p.thumb && "no-img", imgFailed && "img-fail")}>
        {p.thumb && !imgFailed && (
          <img src={p.thumb} alt="" loading="lazy" onError={() => setImgFailed(true)} />
        )}
        <span className="ov-kol-plat">{p.platformLabel || p.platform}</span>
      </span>
      <span className="ov-kol-body">
        <span className="ov-kol-who">
          {p.avatar && !avaFailed && (
            <img
              className="ov-kol-ava"
              src={p.avatar}
              alt=""
              loading="lazy"
              onError={() => setAvaFailed(true)}
            />
          )}
          <b>{p.nickname || p.username}</b>
        </span>
        <span className="ov-kol-stats">
          <b>{fmtCompact(p.views)}</b> views
          {p.likes ? ` · ${fmtCompact(p.likes)} likes` : ""}
        </span>
        {p.posted && <span className="ov-kol-date muted">{p.posted}</span>}
      </span>
    </a>
  );
}
